// User-level display preferences (PRD 50).
//
// Today it carries one: whether this person wants to see the Personal side of
// the app at all. A business customer who will never track their groceries in
// Greenback can turn it off and stop being shown six menu rows and a landing
// page they do not use.
//
// A PREFERENCE, NOT A PERMISSION. Nothing here gates anything: every personal
// route still resolves, every connected account still syncs, and no data is
// deleted or hidden from a report. It decides what gets DRAWN.
//
// A context rather than the module-level store PRD 45 needed, because this one
// is user-scoped: the provider sits above the router, so the sidebar and the
// landing redirect both reach it the ordinary way. (Experiences needed a store
// only because they are business-scoped and BusinessProvider is a CHILD of the
// route that renders the sidebar.)

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { appPreferencesService } from '../services/appPreferencesService';
import { useAuth } from './AuthContext';

interface AppPreferencesValue {
  /**
   * The stored preference, before the "is there a business side to fall back
   * on" rule is applied. Read usePersonalSideHidden() to decide what to draw;
   * read this only where the SETTING itself is shown.
   */
  personalHidden: boolean;
  /** True once a server answer has settled this session. */
  hasLoaded: boolean;
  /** Optimistic; rolls back and rethrows if the save fails. */
  setPersonalHidden: (next: boolean) => Promise<void>;
}

// No provider means today's behaviour, which keeps every existing test and any
// tree that does not mount the provider exactly as it was.
const AppPreferencesContext = createContext<AppPreferencesValue>({
  personalHidden: false,
  hasLoaded: false,
  setPersonalHidden: async () => {},
});

// THE MIRROR, and why it exists: the sidebar renders before any fetch settles.
// Without a value to read synchronously, someone who hid the personal side
// would watch it flash into view and vanish on every single page load.
//
// Keyed by user uuid rather than cleared on sign-out. That is the stronger
// guarantee — the next person to use this browser cannot read the last
// person's answer even if a clear were missed — and it also means a returning
// user gets a correct first frame instead of one flash per sign-in.
const mirrorKey = (uuid: string | undefined) => (uuid ? `gb_personal_hidden:${uuid}` : null);

const readMirror = (uuid: string | undefined): boolean => {
  const key = mirrorKey(uuid);
  if (!key) return false;
  try {
    return window.localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
};

const writeMirror = (uuid: string | undefined, value: boolean): void => {
  const key = mirrorKey(uuid);
  if (!key) return;
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    // Private mode, or storage full. The server still has the answer.
  }
};

export const AppPreferencesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const uuid = user?.uuid;
  const authToken = user?.authToken;

  const [personalHidden, setPersonalHiddenState] = useState<boolean>(() => readMirror(uuid));
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    if (!authToken) {
      // Signed out: back to showing everything, without touching any mirror —
      // the uuid key keeps each person's answer to themselves.
      setPersonalHiddenState(false);
      setHasLoaded(false);
      return;
    }

    setPersonalHiddenState(readMirror(uuid));

    let cancelled = false;
    appPreferencesService
      .get()
      .then(prefs => {
        if (cancelled) return;
        setPersonalHiddenState(prefs.personal_hidden);
        writeMirror(uuid, prefs.personal_hidden);
        setHasLoaded(true);
      })
      .catch(() => {
        // A failed fetch keeps the mirror rather than reverting: the last
        // known answer is a better guess than "show everything", and this is
        // presentation, so being wrong for one page load costs nothing.
        if (!cancelled) setHasLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, [authToken, uuid]);

  const setPersonalHidden = useCallback(async (next: boolean) => {
    const previous = personalHidden;
    setPersonalHiddenState(next);
    writeMirror(uuid, next);
    try {
      const saved = await appPreferencesService.update({ personal_hidden: next });
      setPersonalHiddenState(saved.personal_hidden);
      writeMirror(uuid, saved.personal_hidden);
    } catch (error) {
      // Roll back rather than leave a lie the next page load would contradict.
      setPersonalHiddenState(previous);
      writeMirror(uuid, previous);
      throw error;
    }
  }, [personalHidden, uuid]);

  return (
    <AppPreferencesContext.Provider value={{ personalHidden, hasLoaded, setPersonalHidden }}>
      {children}
    </AppPreferencesContext.Provider>
  );
};

export function useAppPreferences(): AppPreferencesValue {
  return useContext(AppPreferencesContext);
}
